import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  TextField,
  Collapse,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import BarChartIcon from "@mui/icons-material/BarChart";
import SchoolIcon from "@mui/icons-material/School";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AnswerRow from "../ActiveSessionsPage/AnswerRow";
import Header from "../../../components/Header/Header";
import useAxios from "../../../hooks/useAxios";
import "./QuizSpecificPage.css";
import StyledSelect from "../../../components/common/StyledComponents/StyledSelect";
import ContentHeader from "../../../components/common/ContentHeader/ContentHeader";
import ReportModal from "../../../components/Reporting/Modals/ReportModal/ReportModal";
import ArticleIcon from "@mui/icons-material/Article"; 

const navLinks = [];

function QuizSpecificPage() {
  const axiosInstance = useAxios();
  const [teachers, setTeachers] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [quizSearch, setQuizSearch] = useState("");
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await axiosInstance.get("/users/teachers");
        if (response.data.success) {
          setTeachers(response.data.teachers);

          // Extract quizzes
          const allQuizzes = response.data.teachers.flatMap((teacher) =>
            teacher.quizzes.map((quiz) => ({
              id: quiz.id,
              title: quiz.title,
              ownerName: teacher.full_name,
              uniqueId: quiz.unique_identifier,
              updatedAt: quiz.updated_at,
            }))
          );
          setQuizzes(allQuizzes);
        }
      } catch (error) {
        toast.error("Error fetching teachers and quizzes.");
      }
    };
    fetchTeachers();
  }, []);

  const handleQuizSelect = async (quizId) => {
    setSelectedQuiz(quizId);
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/school-report/quiz/${quizId}`);
      setReport(response.data);
      toast.success("Quiz report loaded successfully!");
    } catch (error) {
      toast.error("Error fetching quiz report.");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (sessionId) => {
    setExpandedSessionId((prev) => (prev === sessionId ? null : sessionId));
  };

  const filteredQuizzes = quizzes.filter((quiz) =>
    quiz.title.toLowerCase().includes(quizSearch.toLowerCase())
  );

  const handleViewReportClick = async (sessionId) => {
    try {
      setLoading(true);  // You can reuse the same `loading` or create a new piece of state
      const response = await axiosInstance.get(`/sessions/${sessionId}/report`);
      if (response.data.success) {
        setCurrentReport(response.data.report);
        setReportModalOpen(true);
      } else {
        toast.error(response.data.message || "Failed to fetch report.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };
  

  return (
    <div data-theme={darkMode ? "dark" : "light"}>
      <Header navLinks={navLinks} isScrollEffect={false} />
      <Container className="quiz-report-container page-content">
        <Box className="page-header">
          <ContentHeader title="Select Assessment" />
          <Box>
            <StyledSelect
              value={selectedQuiz || ""}
              onChange={(e) => handleQuizSelect(e.target.value)}
              displayEmpty
              className="quiz-select"
            >
              <MenuItem value="" disabled>
                Select an Assessment
              </MenuItem>
              {filteredQuizzes.map((quiz) => (
                <MenuItem key={quiz.id} value={quiz.id}>
                  {quiz.title} - {quiz.ownerName}
                </MenuItem>
              ))}
            </StyledSelect>
          </Box>
        </Box>

        {loading && (
          <Box className="loading-container">
            <CircularProgress />
            <Typography>Loading report...</Typography>
          </Box>
        )}

        {report && (
        <Box>
            {/* Teacher Details Section */}
            <Paper elevation={2} className="teacher-details">
            <Box className="details-header">
                <Box className="avatar-container">
                <Typography className="avatar-initials">
                    {report.quiz.owner.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")} {/* Owner Initials */}
                </Typography>
                </Box>
                <Box>
                <Typography variant="h6" className="teacher-name">
                    {report.quiz.owner.name}
                </Typography>
                <Typography variant="body2" className="teacher-role">
                    Teacher
                </Typography>
                <Typography variant="body2" className="teacher-sessions">
                    Total Sessions: <strong>{report.total_sessions}</strong>
                </Typography>
                </Box>
            </Box>
            <Box className="contact-details">
                <Typography>
                <Tooltip title="Email" arrow>
                    ✉️{" "}
                    <a href={`mailto:${report.quiz.owner.email}`} className="contact-link">
                    {report.quiz.owner.email}
                    </a>
                </Tooltip>
                </Typography>
                <Typography>
                <Tooltip title="Phone Number" arrow>
                    📞{" "}
                    <a href={`tel:${report.quiz.owner.phone_number}`} className="contact-link">
                    {report.quiz.owner.phone_number}
                    </a>
                </Tooltip>
                </Typography>
            </Box>
            </Paper>

            {/* Sessions Data */}
            {report.sessions.map((session) => (
            <Paper elevation={2} key={session.session_id} className="session-card">
                <Box className="session-header">
                  <Box className="session-info-left">
                      <Typography variant="subtitle1" className="session-user">
                          {session.user.username}
                      </Typography>
                      <Typography variant="body2" className="session-attempt">
                          Attempt: {session.attempt}
                      </Typography>
                      <Typography
                      variant="body2"
                      className="session-evaluation"
                      style={{
                          color:
                          session.final_evaluation > 75
                              ? "green"
                              : session.final_evaluation > 50
                              ? "orange"
                              : "red",
                      }}
                      >
                      Final Evaluation: {session.final_evaluation}%
                      </Typography>
                  </Box>
                  <Box className="session-actions-right">
                    <IconButton onClick={() => toggleExpand(session.session_id)}>
                        {expandedSessionId === session.session_id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    {/* NEW: Report button */}
                    <Tooltip title="View AI Report">
                      <IconButton onClick={() => handleViewReportClick(session.session_id)}>
                        <ArticleIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Collapse in={expandedSessionId === session.session_id} timeout="auto" unmountOnExit>
                <Box className="answers-container">
                    {session.user_answers.map((answer, index) => (
                    <AnswerRow key={index} answer={answer} />
                    ))}
                </Box>
                </Collapse>
            </Paper>
            ))}
        </Box>
        )}

      </Container>
      <ToastContainer />
      <ReportModal
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        report={currentReport}
      />
    </div>
  );
}

export default QuizSpecificPage;
